import React, { useEffect, useState, useMemo } from "react";
import { getApi } from "../../../Services/apiService";
import {
  getMemberBirthCalendarParts,
  getMemberDobTimestamp,
} from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardDeceasedMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardDeceasedAngel,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import {
  extractAgeMilestoneGroups,
  filterAgeMilestoneMembers,
} from "../../../Utils/dashboardMonthlyAgeMilestones";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";
import {
  DASHBOARD_PERIOD_ALL_MONTHS as ALL_MONTHS,
  DASHBOARD_PERIOD_MONTH_NAMES,
  DASHBOARD_PERIOD_MONTH_OPTIONS,
} from "./DashboardPeriodPickers";
import {
  buildAgeMilestoneGroupsForYear,
  getDashboardMilestoneYearOptions,
} from "../../../Utils/dashboardAgeMilestoneYears";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";

// Row size on the compact layout — must match the phone step-up at the end
// of App.css, or the measured name column comes out too narrow.
const MILESTONE_AGES_COMPACT_ROW_FONT = "18px";

function AgesMilestone() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [legendLines, setLegendLines] = useState([]);
  const [loading, setLoading] = useState(true);
  // The current year's groups, exactly as the API returns them, and every
  // member in the tree — the pool any other year is bucketed out of.
  const [groupedMilestones, setGroupedMilestones] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [headOfLineColumnWidth, setHeadOfLineColumnWidth] = useState("auto");
  const [sortBy, setSortBy] = useState("youngest"); // "none", "oldest", "youngest", "alpha-asc", "alpha-desc", "color", "death-asc"
  const [searchQuery, setSearchQuery] = useState("");
  // The year whose milestones are listed. It opens on the current one — the
  // year the API answers for — and runs a few years on from there.
  const [year, setYear] = useState(() => new Date().getFullYear());
  // The month within that year — 1–12, or ALL_MONTHS for the whole year, month
  // by month. It opens on All Months, which is what makes this the yearly
  // report; picking a month narrows the same year down to it, exactly as the
  // Monthly Milestone Ages report does.
  const [month, setMonth] = useState(ALL_MONTHS);
  const [isLargeView, setIsLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Milestone ages expected from API:
  // 1, 5, 7, 10, 15, 16, 18, 20, 21, 25, and multiples of 5 up to 120, plus 62
  // The API already groups the current year's data by these milestones, so
  // nothing is calculated here for it. It takes no year of its own, though, so
  // any other year the picker offers is grouped by the same list in the
  // frontend — see Utils/dashboardAgeMilestoneYears.

  // Fetch ages milestone data
  useEffect(() => {
    const fetchAgesMilestone = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setAllMembers([]);
          setGroupedMilestones([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          const members = extractAllMembersFromStatisticsResponse(response.data);
          const heads = buildHeadsOfLineIdByColorMap(
            extractHeadsOfLineFromStatisticsResponse(response.data),
            members
          );
          setHeadsMap(heads);
          // The phone's footer key. Built from the whole tree, not this page's
          // rows, so it lists the same family lines as every other detail page.
          setLegendLines(buildDashboardColorLegendLines(members, heads));
          // Every member in the tree, kept so a year other than the current one
          // can be bucketed off their dates of birth — the endpoint answers for
          // the current year only and takes no year of its own. In-laws are
          // dropped here as they are from the API's own groups, so a computed
          // year lists the same people the current one does.
          setAllMembers(filterAgeMilestoneMembers(members));
          // The API's own grouping of the current year: data.age_milestones.details,
          // each entry { milestone, count, details: [people] }, minus the in-laws.
          // Sorted by milestone descending (oldest first).
          setGroupedMilestones(
            extractAgeMilestoneGroups(response.data).sort(
              (a, b) => b.milestone - a.milestone
            )
          );
        } else {
          setAllMembers([]);
          setGroupedMilestones([]);
        }
      } catch (error) {
        console.error("Error fetching ages milestone:", error);
        setAllMembers([]);
        setGroupedMilestones([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgesMilestone();
  }, []);

  const formatPersonName = (member) => formatDashboardMemberDisplayName(member);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const yearOptions = useMemo(
    () => getDashboardMilestoneYearOptions(year),
    [year]
  );

  // The year on screen. The current one is the API's own grouping, untouched —
  // it is the year the endpoint answers for. Any other is bucketed here from
  // the members' dates of birth, into the same shape.
  const yearGroupedMilestones = useMemo(() => {
    if (year === currentYear) return groupedMilestones;
    return buildAgeMilestoneGroupsForYear(
      allMembers,
      year,
      groupedMilestones.flatMap((group) => group?.details || [])
    );
  }, [year, currentYear, groupedMilestones, allMembers]);

  // Flat list of everyone on screen for the year — what the column measuring
  // below sizes the name and head-of-line columns against, and what says
  // whether the year holds anything at all.
  const agesMilestone = useMemo(
    () => yearGroupedMilestones.flatMap((group) => group?.details || []),
    [yearGroupedMilestones]
  );

  // Helper function to render member row with 2 columns
  const renderMemberRow = (member) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member);
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.family_line_hex_code || member?.hex_code || "#000000";
    const colorName = member?.family_line_color || member?.color_name || "";

    return (
      <div className={`ages-milestone-member-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Person Name */}
        <div className={`ages-milestone-member-name ${isLargeView ? 'large-view' : ''}`} style={{ minWidth: nameColumnWidth }}>
          <DashboardNameMarkerGroup>
          <DashboardPhotoName
            {...getDashboardMemberNamePhotoUrls(member)}
            onViewPhoto={openPhoto}
          >
            <DashboardMemberNameLabel
              baseName={baseName}
              descendantSpouseName={descendantSpouseName}
            />
          </DashboardPhotoName>
          {/* No in-law "*": the report lists no in-laws to mark. */}
          {isDashboardDeceasedMember(member) && (
            <DashboardDeceasedAngel isLargeView={isLargeView} />
          )}
          {multipleBirthSup && (
            <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>{multipleBirthSup}</sup>
          )}
          </DashboardNameMarkerGroup>
        </div>
        
        {/* Column 2: Head of Line Name and Color. Rendered on both layouts — the
            compact one scrolls sideways to reach it rather than dropping it. */}
        <div
          className={`ages-milestone-member-head ${isLargeView ? 'large-view' : ''}`}
          style={{ width: headOfLineColumnWidth }}
        >
          <div className={isLargeView ? 'large-view-text' : ''}>
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
        </div>
      </div>
    );
  };

  // Measure the actual width of name and head of line elements after render
  useEffect(() => {
    if (agesMilestone.length === 0 || loading) return;
    
    // Helper functions (local copies for useEffect)
    const getHeadOfLineNameLocal = (row) =>
      getDashboardFamilyLineHeadDisplayName(row, headsMap);
    
    // Use appropriate font size based on view mode
    const fontSize = isLargeView ? '24px' : isCompactLayout ? MILESTONE_AGES_COMPACT_ROW_FONT : '16px';
    // Keep the name column at least as wide as the widest name (+ a small
    // sub-pixel safety margin). A negative buffer would let the longest name
    // overflow its min-width and grow the row, pushing that row's head-of-line
    // column out of vertical alignment with shorter-name rows.
    const nameColBuffer = 2;
    
    // Create temporary elements for measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = 'inherit';
    tempDiv.style.display = 'flex';
    tempDiv.style.alignItems = 'center';
    document.body.appendChild(tempDiv);
    
    const chMeasure = document.createElement('div');
    chMeasure.style.position = 'absolute';
    chMeasure.style.visibility = 'hidden';
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = 'inherit';
    chMeasure.textContent = '0';
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.offsetWidth;

    // Lead-in on the name: 20px on the wide layout, none on the compact one,
    // where the column runs flush to the left margin (see the matching
    // `padding-left` in the media block). Measure it the way it renders, or the
    // longest name outgrows the column's min-width and widens just that row.
    const nameColPaddingLeft = isCompactLayout ? 0 : 20;

    let maxNameWidth = 0;
    let maxHeadOfLineWidth = 0;
    
    agesMilestone.forEach((member) => {
      const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member);
      const supText = getMultipleBirthSuperscriptText(member);

      tempDiv.innerHTML = "";
      tempDiv.style.paddingLeft = "0";

      const nameSpan = document.createElement("span");
      nameSpan.style.paddingLeft = `${nameColPaddingLeft}px`;
      nameSpan.textContent = baseName;
      if (descendantSpouseName) {
        nameSpan.appendChild(document.createTextNode(" ("));
        const strong = document.createElement("strong");
        strong.className = "dashboard-descendant-spouse-name";
        strong.textContent = descendantSpouseName;
        nameSpan.appendChild(strong);
        nameSpan.appendChild(document.createTextNode(")"));
      }
      tempDiv.appendChild(nameSpan);

      if (supText) {
        const sup = document.createElement("sup");
        sup.className = "dashboard-multiple-birth-sup";
        sup.textContent = supText;
        sup.style.fontSize = "0.75em";
        sup.style.fontWeight = "700";
        tempDiv.appendChild(sup);
      }

      // The angel is a fixed-size image (see DashboardDeceasedAngel), so a box
      // of the same size stands in for it.
      if (isDashboardDeceasedMember(member)) {
        const angel = document.createElement("span");
        angel.style.display = "inline-block";
        angel.style.width = isLargeView ? "18px" : "12px";
        angel.style.flexShrink = "0";
        tempDiv.appendChild(angel);
      }

      const nameWidth = tempDiv.offsetWidth;
      if (nameWidth > maxNameWidth) {
        maxNameWidth = nameWidth;
      }
      
      // Measure head of line width
      const headOfLineName = getHeadOfLineNameLocal(member) || "Start/Top of Line";
      const colorName = member?.family_line_color || member?.color_name || "";
      const colorText = colorName ? ` (●  ${colorName} Line)` : "";
      const headOfLineText = `${headOfLineName}${colorText}`;
      tempDiv.style.display = "block";

      tempDiv.textContent = headOfLineText;
      const headOfLineWidth = tempDiv.offsetWidth;
      if (headOfLineWidth > maxHeadOfLineWidth) {
        maxHeadOfLineWidth = headOfLineWidth;
      }

      tempDiv.style.display = "flex";
    });
    
    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    
    const headOfLineWidthInCh = Math.ceil(maxHeadOfLineWidth / chWidth);
    setNameColumnWidth(`${maxNameWidth + nameColBuffer}px`);
    setHeadOfLineColumnWidth(`${headOfLineWidthInCh}ch`);
  }, [agesMilestone, loading, isLargeView, isCompactLayout]);

  const getMemberLineColor = (member) =>
    (member?.family_line_color || member?.color_name || "").toLowerCase();

  // The picked month narrows each of the year's groups to the members whose
  // milestone birthday falls in it, then the search box narrows what is left; a
  // group nobody in it matches drops out with the rest of the empty ones. A
  // member with no date of birth cannot be placed in a month, so a picked month
  // leaves them out — All Months keeps every one of them.
  const filteredGroupedMilestones = useMemo(() => {
    if (!yearGroupedMilestones.length) return [];
    const monthIndex = month === ALL_MONTHS ? null : month - 1;
    return yearGroupedMilestones
      .map((group) => {
        const inMonth =
          monthIndex === null
            ? group.details || []
            : (group.details || []).filter(
                (member) =>
                  getMemberBirthCalendarParts(member)?.month === monthIndex
              );
        return {
          ...group,
          details: filterDashboardMembersBySearch(
            inMonth,
            searchQuery,
            formatPersonName
          ),
        };
      })
      .filter((group) => group.details.length > 0);
  }, [yearGroupedMilestones, month, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredTotal = useMemo(
    () =>
      filteredGroupedMilestones.reduce(
        (sum, group) => sum + (group.details?.length || 0),
        0
      ),
    [filteredGroupedMilestones]
  );

  // Sort grouped milestones based on sortBy
  const sortedGroupedMilestones = useMemo(() => {
    if (!filteredGroupedMilestones.length) return [];
    
    // Create deep copy to avoid mutating original
    const sorted = filteredGroupedMilestones.map(group => ({
      ...group,
      details: group.details ? [...group.details] : []
    }));
    
    // Sort groups by milestone (for oldest/youngest, color, or default to oldest for alpha)
    if (
      sortBy === "oldest" ||
      sortBy === "alpha-asc" ||
      sortBy === "alpha-desc" ||
      sortBy === "color" ||
      sortBy === "death-asc" ||
      sortBy === "none"
    ) {
      // Sort by milestone in descending order (oldest first)
      sorted.sort((a, b) => b.milestone - a.milestone);
    } else if (sortBy === "youngest") {
      // Sort by milestone in ascending order (youngest first)
      sorted.sort((a, b) => a.milestone - b.milestone);
    }
    
    // Sort members within each group based on sortBy
    sorted.forEach((group) => {
      if (group?.details && Array.isArray(group.details)) {
        if (sortBy === "death-asc") {
          group.details = sortDashboardMembersByDeathDate(group.details);
        } else if (sortBy === "alpha-asc") {
          group.details.sort((a, b) => {
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
        } else if (sortBy === "alpha-desc") {
          group.details.sort((a, b) => {
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameB.localeCompare(nameA);
          });
        } else if (sortBy === "oldest") {
          group.details.sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);
            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobA - dobB;
          });
        } else if (sortBy === "youngest") {
          group.details.sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);
            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobB - dobA;
          });
        } else if (sortBy === "color") {
          group.details.sort((a, b) => {
            const colorA = getMemberLineColor(a);
            const colorB = getMemberLineColor(b);
            if (colorA !== colorB) {
              return colorA.localeCompare(colorB);
            }
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
        }
      }
    });
    
    return sorted;
  }, [filteredGroupedMilestones, sortBy]);

  // With All Months picked the list runs month by month, as Birthdays this
  // Month does: each month heads its own run of age groups, in calendar order,
  // with its own count. The age groups inside a month keep the order the sort
  // put them in, and their Grp Totals then count that month rather than the
  // year. A member with no date of birth belongs to no month; rather than drop
  // them from a list that counts them, they trail the months under their own
  // heading. Null for a single month, which needs no sections at all.
  const monthSections = useMemo(() => {
    if (month !== ALL_MONTHS) return null;

    const sections = DASHBOARD_PERIOD_MONTH_NAMES.map((name, index) => ({
      key: String(index),
      name,
      groups: [],
      total: 0,
    }));
    const undated = { key: "undated", name: "Date of Birth Unknown", groups: [], total: 0 };

    sortedGroupedMilestones.forEach((group) => {
      const byMonth = new Map();
      (group.details || []).forEach((member) => {
        const monthIndex = getMemberBirthCalendarParts(member)?.month;
        const key = Number.isInteger(monthIndex) ? monthIndex : "undated";
        if (!byMonth.has(key)) byMonth.set(key, []);
        byMonth.get(key).push(member);
      });

      byMonth.forEach((members, key) => {
        const section = key === "undated" ? undated : sections[key];
        if (!section) return;
        section.groups.push({ ...group, details: members });
        section.total += members.length;
      });
    });

    return [...sections, undated].filter((section) => section.total > 0);
  }, [month, sortedGroupedMilestones]);

  /** Ages / Grp Totals column labels — repeated under each month on All Months. */
  const renderColumnHeader = (keySuffix = "") => (
    <div
      key={keySuffix ? `column-header-${keySuffix}` : undefined}
      className="ages-milestone-column-header ages-milestone-column-header-inline"
    >
      <span className="ages-milestone-column-label">Ages</span>
      <span className="ages-milestone-column-header-spacer" aria-hidden="true" />
      <span className="ages-milestone-column-label ages-milestone-column-label-totals">
        Grp Totals
      </span>
    </div>
  );

  /** One milestone age: its badge, the rule across, the group total and the rows. */
  const renderMilestoneGroup = (milestoneGroup, keyPrefix = "") => {
    const milestone = milestoneGroup?.milestone;
    const members = milestoneGroup?.details || [];

    return (
      <div key={`${keyPrefix}${milestone}`} className="ages-milestone-group">
        {/* Milestone Header */}
        <div className="ages-milestone-group-header">
          <span className="ages-milestone-badge">
            {milestone}
          </span>
          <div className="ages-milestone-divider"></div>
          <span className="ages-milestone-count-badge">
            {members.length}
          </span>
        </div>

        {/* Members List - Centered between badges */}
        <div className="ages-milestone-members-container">
          <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
            {milestone}
          </span>
          <div className="ages-milestone-members-list">
            {members.map((member, index) => (
              <div key={member?.id || index}>
                {renderMemberRow(member)}
              </div>
            ))}
          </div>
          <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
            {members.length}
          </span>
        </div>
      </div>
    );
  };

  // What heads the list when the report is not on its default view: the month
  // picked, the year picked, or both. Nothing on All Months of the current year
  // — that is what the title already names.
  const periodHeading = useMemo(() => {
    const monthName =
      month === ALL_MONTHS ? "" : DASHBOARD_PERIOD_MONTH_NAMES[month - 1] || "";
    const yearName = year === currentYear ? "" : String(year);
    return [monthName, yearName].filter(Boolean).join(" ");
  }, [month, year, currentYear]);

  return (
    <div className={`dashboard-container ages-milestone-container current-year-milestone-ages-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box ages-milestone-box dashboard-detail-box">
        <div className="ages-milestone-header">
          <div className="ages-milestone-header-left">
            <DashboardBackButton />
            <h3 className="ages-milestone-title">Milestone Ages this Year</h3>
            <div className="ages-milestone-count">
              {filteredTotal}
            </div>
            {!loading && agesMilestone.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                endAtControl='[data-sort="oldest"]'
              />
            )}
            {!loading && agesMilestone.length > 0 && (
              <DashboardDetailSortFilterBar
                className="ages-milestone-sort-toggle"
                buttonClassName="ages-milestone-sort-btn"
                sortBy={sortBy}
                onSortChange={setSortBy}
                defaultSort="youngest"
                showMemberFilters={false}
                leadingSortButtons={
                  <div
                    className="dashboard-detail-period"
                    role="group"
                    aria-label="Report period"
                  >
                    {/* Month then Year, the order every other dashboard list
                        picks its period in. The report opens on All Months, so
                        the year reads whole and month by month; picking a month
                        narrows that same year down to it. */}
                    <DashboardPeriodDropdown
                      label="Month"
                      options={DASHBOARD_PERIOD_MONTH_OPTIONS}
                      value={month}
                      onChange={(next) =>
                        setMonth(
                          next === ALL_MONTHS ? ALL_MONTHS : parseInt(next, 10)
                        )
                      }
                    />
                    <DashboardPeriodDropdown
                      label="Year"
                      options={yearOptions}
                      value={year}
                      onChange={(next) => setYear(parseInt(next, 10))}
                    />
                    <span
                      className="dashboard-detail-sort-divider"
                      aria-hidden="true"
                    />
                  </div>
                }
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="ages-milestone-empty-state">
            <p>Loading ages milestone...</p>
          </div>
        ) : agesMilestone.length === 0 ? (
          <div className="ages-milestone-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : filteredTotal === 0 ? (
          <div className="ages-milestone-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <>
            {/* The period picked, named over the list — as In Memoriam heads
                each of its months. Nothing to head on All Months of the current
                year: that is what the title already names and the report reads
                by default. No count on it either, since the one beside the
                title already counts the period. Outside the sideways scroller,
                so it stays at the card's left edge. */}
            {periodHeading && (
              <div
                className={`ages-milestone-month-header ${
                  isLargeView ? "large-view" : ""
                }`}
              >
                <span className="ages-milestone-month-name dashboard-section-heading">{periodHeading}</span>
              </div>
            )}
            {/* Compact layout scrolls sideways once the head-of-line column pushes
                rows past the card. Column headers ride inside the scroller under
                each month (or once below the period heading) so "Grp Totals"
                stays over its badge instead of decoupling from it. Both wrappers
                are `display: contents` above the breakpoint. */}
            <div className="ages-milestone-hscroll">
            <div className="ages-milestone-hscroll-track">
            <div className="ages-milestone-list">
            {monthSections
              ? monthSections.map((section) => (
                  <React.Fragment key={section.key}>
                    {/* Each month names itself and counts its own people — the
                        count beside the title is the whole year's. Sticks to
                        the left edge while the compact layout scrolls sideways
                        to the head-of-line column. */}
                    <div
                      className={`ages-milestone-month-header ages-milestone-month-header-inline ${
                        isLargeView ? "large-view" : ""
                      }`}
                    >
                      <span className="ages-milestone-month-name dashboard-section-heading">
                        {section.name}{" "}<span className="dashboard-section-heading-count">({section.total})</span>
                      </span>
                    </div>
                    {renderColumnHeader(section.key)}
                    {section.groups.map((group) =>
                      renderMilestoneGroup(group, `${section.key}-`)
                    )}
                  </React.Fragment>
                ))
              : (
                  <>
                    {renderColumnHeader()}
                    {sortedGroupedMilestones.map((group) =>
                      renderMilestoneGroup(group)
                    )}
                  </>
                )}
            </div>
            </div>
            </div>
            {/* Marker legend keyed to the rows on screen, as on the other detail
                pages, then the phone's family-lines key (as on Family Members). */}
            <div className="ages-milestone-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedGroupedMilestones.map((group) => group.details)
                )}
                showInLaws={false}
              />
              {isCompactLayout && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default AgesMilestone;

